/**
 * Stages the batch UI no longer shows (product decision 2026-10-16: "remove the Image check stage
 * and the Images tab from the UI fully"). The backend still runs IMAGE_GATE, so its counts are
 * FOLDED into Feed intake (IMPORT) rather than dropped — see `hideImageCheckStage` in
 * `mappers/run-funnel.mapper.ts`. This module is the one home of that vocabulary.
 */

/** The hidden stage's code, as catalog-service reports it. */
export const IMAGE_CHECK_STAGE = 'IMAGE_GATE';

/** The visible stage the hidden one's counts and reasons are folded into (the stage before it). */
export const IMAGE_CHECK_FOLD_TARGET = 'IMPORT';

/** True for a stage code the UI must never render as its own row, pane, tab or link. */
export function isHiddenStage(stage: string | null | undefined): boolean {
  return stage === IMAGE_CHECK_STAGE;
}

/**
 * The stage a code is SHOWN as: a hidden stage reads as the visible stage it folds into, every
 * other code is unchanged. Every stage label goes through this, so a run sitting at IMAGE_GATE
 * reads "Feed intake" (Now / Next, header, recovery, issues) and no text ever names the step.
 */
export function visibleStage(stage: string): string {
  return isHiddenStage(stage) ? IMAGE_CHECK_FOLD_TARGET : stage;
}

/** Stage codes minus hidden ones, order kept — for any list a person picks from or reads. */
export function withoutHiddenStages<T extends string>(stages: readonly T[]): T[] {
  return stages.filter((stage) => !isHiddenStage(stage));
}

/** Rows minus hidden stages, except one hidden row the caller must keep (e.g. the failure point,
 *  the only row carrying its reason — it then reads as the stage it folds into). */
export function withoutHiddenRows<T extends { stage: string }>(
  rows: readonly T[],
  keep?: string | null,
): T[] {
  return rows.filter((row) => !isHiddenStage(row.stage) || row.stage === keep);
}

/** Statuses of a hidden stage that must stay visible on the stage it folds into: still working,
 *  waiting, or stopped badly — so Feed intake never reads "done" while the hidden stage is not. */
const FOLD_STATUS_OVERRIDES = new Set([
  'RUNNING',
  'WAITING',
  'AWAITING_GATE',
  'FAILED',
  'RECONCILE_FAILED',
  'CANCELLED',
  'TERMINATED',
]);

/** The folded stage's status (worse outcome wins): the target's own, unless it finished cleanly
 *  while the hidden stage is still running / waiting / failed — then the hidden one's is honest. */
export function foldedStageStatus(target: string, hidden: string): string {
  return target === 'COMPLETED' && FOLD_STATUS_OVERRIDES.has(hidden) ? hidden : target;
}

/** Marks a reason folded in from Image check, so its label reads "Images · …". */
const IMAGE_CHECK_CODE_PREFIX = 'AT_IMAGE_CHECK:';

/** Prefixes a folded Image check reason code (an uncoded row keeps its `kind` as the inner code,
 *  so it still says what happened instead of reading as "not explained"). */
export function toImageCheckCode(code: string | null, kind: string): string {
  return `${IMAGE_CHECK_CODE_PREFIX}${code ?? kind}`;
}

/** The original code inside a folded Image check reason, or `null` for any other code. */
export function fromImageCheckCode(code: string): string | null {
  return code.startsWith(IMAGE_CHECK_CODE_PREFIX)
    ? code.slice(IMAGE_CHECK_CODE_PREFIX.length)
    : null;
}

// ---------------------------------------------------------------------------
// Approve & publish fold (product decision 2026-10-16) — FUNNEL ONLY. The Summary funnel shows
// APPROVAL and DELIVERY as ONE stage, "Approve & publish to [logo]": DELIVERY's row folds into
// APPROVAL (see `foldPublishIntoApproval` in `mappers/run-funnel.mapper.ts`). Unlike Image check,
// DELIVERY is NOT a hidden stage anywhere else — batch history, the progress card and the phase
// strip still name it — so nothing here feeds `isHiddenStage` / `visibleStage`.
// ---------------------------------------------------------------------------

/** The stage whose funnel row folds into the approval stage. */
export const PUBLISH_STAGE = 'DELIVERY';

/** The visible funnel stage that carries both steps (its code keeps every ⑦/②/⑧ read working). */
export const PUBLISH_FOLD_TARGET = 'APPROVAL';

/** The funnel pane a `?fStage=` deep link opens: DELIVERY opens the combined Approve & publish
 *  pane, every other code is unchanged. */
export function funnelPaneStage(stage: string): string {
  return stage === PUBLISH_STAGE ? PUBLISH_FOLD_TARGET : stage;
}

/** Marks a reason folded in from the approval step, so its label reads "At approval: …". */
const APPROVAL_STEP_CODE_PREFIX = 'AT_APPROVAL:';

/** Marks a reason folded in from the publish step, so its label reads "At publish: …". */
const PUBLISH_STEP_CODE_PREFIX = 'AT_PUBLISH:';

/** Prefixes an approval-step reason code (an uncoded row keeps its `kind` as the inner code). */
export function toApprovalStepCode(code: string | null, kind: string): string {
  return `${APPROVAL_STEP_CODE_PREFIX}${code ?? kind}`;
}

/** Prefixes a publish-step reason code (an uncoded row keeps its `kind` as the inner code). */
export function toPublishStepCode(code: string | null, kind: string): string {
  return `${PUBLISH_STEP_CODE_PREFIX}${code ?? kind}`;
}

/** Which fold a prefixed reason came from — drives its "Images · …" / "At approval: …" /
 *  "At publish: …" label. */
export type FoldedCodeStep = 'IMAGE_CHECK' | 'APPROVAL' | 'PUBLISH';

/** A folded reason's step and original code, or `null` for a code no fold prefixed. */
export function foldedCodeParts(code: string): { step: FoldedCodeStep; inner: string } | null {
  const image = fromImageCheckCode(code);
  if (image !== null) return { step: 'IMAGE_CHECK', inner: image };
  if (code.startsWith(APPROVAL_STEP_CODE_PREFIX)) {
    return { step: 'APPROVAL', inner: code.slice(APPROVAL_STEP_CODE_PREFIX.length) };
  }
  if (code.startsWith(PUBLISH_STEP_CODE_PREFIX)) {
    return { step: 'PUBLISH', inner: code.slice(PUBLISH_STEP_CODE_PREFIX.length) };
  }
  return null;
}

/** A reason code with any fold prefix removed — its own semantics (loss, retry, unexplained). */
export function innerFunnelCode(code: string): string {
  return foldedCodeParts(code)?.inner ?? code;
}
